import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from '@store/GlobalStore';
import { useGetPolicy } from '@services/policy';
import { PolicyStatus } from '@models/policy';
import { shallow } from 'zustand/shallow';
import { SubjectivityStatus } from '@lio/rule-engine';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { SubjectivityTable } from '@components/Subjectivities/SubjectivityTable';
import type { SubjectivityTableRow } from '@components/Subjectivities/SubjectivityTable';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';

export const PolicySubjectivitiesTab = () => {
  const { policyId } = useParams();
  const { isInternalUser, isAdmin, isPortalAdmin } = useStore((state) => state.userAuth, shallow);

  const { policy } = useGetPolicy({ policyId });

  const formattedPolicyNumber =
    policy && getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber);

  const rowFormatter = useCallback(
    (rows: SubjectivityTableRow[]): SubjectivityTableRow[] => {
      const closedSubjectivities = rows.filter((it) => it.status === SubjectivityStatus.Closed);

      return rows.map((it) => {
        let isDisabled = false;
        if (
          !isInternalUser &&
          (policy?.status === PolicyStatus.Canceled || policy?.status === PolicyStatus.Rewritten)
        ) {
          isDisabled = true;
        }
        if (
          (policy?.status === PolicyStatus.Active || policy?.status === PolicyStatus.PendingCancelation) &&
          closedSubjectivities.length === rows.length &&
          !isAdmin &&
          !isPortalAdmin
        ) {
          isDisabled = true;
        }
        return { ...it, isDisabled };
      });
    },
    [isInternalUser, policy?.status, isAdmin, isPortalAdmin],
  );

  return (
    <SubjectivityTable
      variant='policy'
      policyId={policyId}
      transactionNumber={formattedPolicyNumber ?? ''}
      rowFormatter={rowFormatter}
      sxProps={{
        tableContainer: { ...styles.dataGridContainer, display: 'block', height: 'fit-content' },
        dataGridContainer: { ...styles.dataGridContainer, borderLeft: 'none', padding: 0, height: 'auto' },
        dataGrid: styles.dataGrid,
      }}
    />
  );
};
